<template>
	<header>
		<div class="header" ref="headerRef">
			<div class="header__container">
				<div class="header__wrapper">
					<div class="header__menu menu">
						<div class="menu__block">
							<nav class="menu__body">
								<ul class="menu__list">
									<li v-for="(label, key) in links" :key="key">
										<router-link
											:to="{ path: '/', hash: `#${key}` }"
											@click="onMenuLinkClick"
										>
											{{ label }}
										</router-link>
									</li>
								</ul>
							</nav>
						</div>
					</div>

					<div class="header__logo">
						<router-link :to="{ path: '/' }">
							<img :src="logo" alt="" />
						</router-link>
					</div>

					<div class="header__button">
						<div class="header__button_square header__button_square-gray"></div>
						<div class="header__button_square header__button_square-black"></div>
						<button
							type="button"
							class="header__button_square header__button_square-black menu__icon"
							@click="toggleMenuHandler"
							aria-label="Открыть меню"
						>
							<span></span>
							<span></span>
							<span></span>
						</button>
					</div>
				</div>
			</div>
		</div>

		<div :style="{ height: headerHeight + 'px' }" aria-hidden="true"></div>
	</header>
</template>

<script setup>
import logo from '@/assets/img/header/logo.svg'
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { toggleMenu, closeMenu } from '@/utils/menuControl'

// --- Реактивные переменные ---
const isMenuOpen = ref(false)
const headerRef = ref(null)
const headerHeight = ref(0)
const isHeaderMenuVisible = ref(false)

/**
 * Переключает меню (открыть/закрыть)
 */
function toggleMenuHandler() {
	toggleMenu()
	isMenuOpen.value = document.documentElement.classList.contains('menu-open')
	isHeaderMenuVisible.value = isMenuOpen.value
}

/**
 * Обработчик клика по ссылке меню
 * Закрывает меню, если оно открыто
 */
function onMenuLinkClick() {
	if (isMenuOpen.value) {
		closeMenu()
		isMenuOpen.value = false
		isHeaderMenuVisible.value = false
	}
}

// --- Массив якорей-ссылок ---
const links = {
	services: 'Услуги',
	case: 'Кейсы',
	team: 'Команда',
	thanks: 'Благодарности',
}

// --- Управление высотой header через ResizeObserver ---
let headerObserver = null

function initHeaderResizeObserver() {
	if (!headerRef.value) return
	headerObserver = new ResizeObserver(() => {
		nextTick(() => {
			if (headerRef.value) headerHeight.value = headerRef.value.offsetHeight
		})
	})
	headerObserver.observe(headerRef.value)
}

function destroyHeaderResizeObserver() {
	if (headerObserver && headerRef.value) {
		headerObserver.unobserve(headerRef.value)
		headerObserver.disconnect()
		headerObserver = null
	}
}

// --- Обработка скролла для header ---
let lastScrollTop = 0
let ticking = false

function updateHeaderOnScroll() {
	const scrollTop = window.pageYOffset || document.documentElement.scrollTop
	const headerEl = headerRef.value
	if (!headerEl) return

	headerEl.classList.toggle('_header-scroll', scrollTop > 0)

	if (window.innerWidth > 991.98) {
		const headerButton = headerEl.querySelector('.header__button .menu__icon')
		if (!headerButton) return

		// ➜ НЕ скрываем кнопку, если меню открыто
		if (document.documentElement.classList.contains('menu-open')) {
			headerButton.style.transform = 'translateY(0)'
			return
		}

		if (scrollTop > 500) {
			if (scrollTop > lastScrollTop) {
				headerButton.style.transform = `translateY(-100%)`
			} else {
				headerButton.style.transform = `translateY(0)`
			}
		} else {
			headerButton.style.transform = 'translateY(0)'
		}
	}

	lastScrollTop = Math.max(scrollTop, 0)
	ticking = false
}

function onScroll() {
	if (!ticking) {
		window.requestAnimationFrame(updateHeaderOnScroll)
		ticking = true
	}
}

// --- Жизненный цикл компонента ---
onMounted(() => {
	initHeaderResizeObserver()
	window.addEventListener('scroll', onScroll)
})

onBeforeUnmount(() => {
	destroyHeaderResizeObserver()
	window.removeEventListener('scroll', onScroll)
})
</script>

<style scoped lang="scss">
.header {
	position: fixed;
	z-index: 999;
	width: 100%;
	top: 0;
	left: 0;
	transition:
		transform 0.8s cubic-bezier(0.77, 0, 0.175, 1),
		all 0.3s ease 0s;

	.menu-open & {
		background-color: #3a3e40;
	}

	@media (max-width: 991.98px) {
		background-color: #3a3e40;
	}

	// .header__container
	&__container {
		padding: 0;
		max-width: 100%;
	}

	// .header__wrapper
	&__wrapper {
		display: flex;
		justify-content: space-between;

		& > * {
			pointer-events: unset;
		}

		@media (min-width: 991.98px) {
			justify-content: center;

			& > * {
				flex: 1 1 33.333%;
			}
		}
	}

	// .header__menu
	&__menu {
		@media (min-width: 991.98px) {
			padding-left: 6rem;
		}

		@media (max-width: 991.98px) {
			display: contents;
		}

		.menu {
			// .menu__block
			&__block {
				@media (min-width: 991.98px) {
					height: 100%;
				}

				@media (max-width: 991.98px) {
					position: absolute;
					top: 100%;
					left: 0;
					width: 100%;
					max-height: calc(100vh - 6.1rem);
					min-height: calc(100vh - 6.1rem);
					padding: 0 2rem;
					overflow-x: hidden;
					overflow-y: auto;
					transform: translateX(-100%);
					transition: transform 0.6s cubic-bezier(0.77, 0, 0.175, 1) 0s;
					background-color: #3a3e40;

					.menu-open & {
						transform: translateX(0%);
					}
				}
			}

			// .menu__body
			&__body {
				@media (min-width: 991.98px) {
					height: 100%;
				}
			}

			// .menu__list
			&__list {
				@media (min-width: 991.98px) {
					display: flex;
					align-items: center;
					height: 100%;
				}

				li {
					@media (min-width: 991.98px) {
						height: 100%;
					}

					@media (max-width: 991.98px) {
						padding: 2rem 0;
					}

					&:not(:last-child) {
						@media (min-width: 991.98px) {
							margin-right: 3.3rem;
						}

						@media (max-width: 991.98px) {
							border-bottom: 1px solid rgba(255, 255, 255, 0.2);
						}
					}

					a {
						width: 100%;
						height: 100%;
						display: flex;
						align-items: center;
						font-weight: 300;
						font-size: 2.2rem;
						// line-height: 2.4rem;
						line-height: 108%;
						transition: color 0.3s ease 0s;

						@media (any-hover: hover) {
							&:hover {
								color: var(--primary-color);
							}
						}

						@media (max-width: 991.98px) {
							font-size: 1.6rem;
						}
					}
				}
			}
		}
	}

	.menu__icon {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		gap: 1.2rem;

		@media (max-width: 991.98px) {
			gap: 0.6rem;
		}

		span {
			width: 1.2rem;
			height: 1.2rem;
			display: block;
			background-color: #f27699;
			position: relative;
			transition: background-color 0.3s ease 0s;

			@media (max-width: 991.98px) {
				width: 0.6rem;
				height: 0.6rem;
			}

			&:nth-child(1),
			&:nth-child(3) {
				&::before,
				&::after {
					content: '';
					position: absolute;
					top: 0;
					display: block;
					width: inherit;
					height: inherit;
					background-color: #f27699;
					opacity: 0;
					transition: opacity 0.3s ease 0s;
				}

				&::before {
					left: -200%;
				}

				&::after {
					right: -200%;
				}
			}
		}

		.menu-open & {
			span {
				&:nth-child(1),
				&:nth-child(3) {
					background-color: transparent;

					&::before,
					&::after {
						opacity: 1;
					}
				}
			}
		}
	}

	// .header__logo
	&__logo {
		display: flex;
		justify-content: center;
		max-width: 26rem;
		width: 100%;

		@media (max-width: 991.98px) {
			justify-content: flex-start;
			max-width: 18rem;
			padding: 2rem 2.4rem;
		}

		a {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 100%;

			img {
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
	}

	&__button {
		display: flex;
		justify-content: flex-end;
		pointer-events: none;

		&_square {
			display: block;
			pointer-events: all;
			width: 12rem;
			height: 12rem;
			transition: transform 0.8s cubic-bezier(0.77, 0, 0.175, 1);

			@media (max-width: 991.98px) {
				width: 6rem;
				height: 6rem;
			}

			&-gray {
				background-color: #424749;
			}

			&-black {
				background-color: #2b2e2f;
			}
		}
	}
}

@media (min-width: 991.98px) {
	.header__menu,
	.header__logo {
		transition: transform 0.8s cubic-bezier(0.77, 0, 0.175, 1);
	}

	.header._header-scroll .header__menu,
	.header._header-scroll .header__logo,
	.header._header-scroll .header__button_square:not(.menu__icon) {
		transform: translateY(-100%);
	}

	.menu-open .header .header__menu,
	.menu-open .header .header__logo,
	.menu-open .header .header__button_square:not(.menu__icon) {
		transform: translateY(0);
	}
}
</style>
